/**
 * Host-aware Applied SuperIntelligence marketing routing.
 *
 * On appliedsuperintelligence.com the nav-less splash is `/`; inner pages are
 * clean slugs (`/overview`, `/method`, …). On app domains the same pages live
 * under /asi-marketing so they do not collide with authenticated /asi/* routes.
 *
 * localStorage ASI_MARKETING_HOST=1 previews dedicated-host routing on any host.
 */

export const OVERVIEW_SLUG = 'overview';

/** Public marketing pages (excludes splash `/`). Legacy `how-it-works` → Method. */
export const MARKETING_SLUGS = [
  OVERVIEW_SLUG,
  'method',
  'learners',
  'faculty',
  'apps',
  'contact',
  'how-it-works', // alias → Method page
];

/** Authenticated product surfaces — keep in sync with AppRoutes /asi/* list. */
export const ASI_PRODUCT_PATHS = {
  templates: '/asi/templates',
  roles: '/asi/roles',
  readiness: '/asi/readiness',
  trainer: '/asi/trainer',
  manager: '/asi/manager',
  globalManagers: '/asi/global-managers',
  /** Teaching launcher (FactPattern, RadioCall, coming-soon tiles). */
  teaching: '/asi/teaching',
};

const MARKETING_BASE = '/asi-marketing';
const HOST_PREVIEW_KEY = 'ASI_MARKETING_HOST';

export function isAsiMarketingHost() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.localStorage?.getItem(HOST_PREVIEW_KEY) === '1') return true;
  } catch {
    // localStorage may be unavailable (private mode).
  }
  const host = window.location.hostname.toLowerCase();
  return (
    host === 'appliedsuperintelligence.com' ||
    host.endsWith('.appliedsuperintelligence.com') ||
    host === 'appliedsuperintelligence-com.web.app' ||
    host === 'appliedsuperintelligence-com.firebaseapp.com'
  );
}

/** Marketing root — nav-less splash. */
export function marketingHubPath() {
  return isAsiMarketingHost() ? '/' : MARKETING_BASE;
}

/** Hub page with nav (not the splash). */
export function marketingOverviewPath() {
  return marketingPagePath(OVERVIEW_SLUG);
}

export function marketingPagePath(slug) {
  if (!slug) return marketingHubPath();
  return isAsiMarketingHost() ? `/${slug}` : `${MARKETING_BASE}/${slug}`;
}

/**
 * Bridge into the main-app sign-in flow, optionally landing on an authenticated
 * /asi/* surface after auth (mirrors FusionMSI authPath({ next })).
 *
 * Uses /auth on every host — ASI marketing never reserves /auth for a product-
 * specific invite gate the way fusionmsi.com does.
 */
export function authPath({ next } = {}) {
  const base = '/auth';
  if (!next) return base;
  return `${base}?next=${encodeURIComponent(next)}`;
}

/** True when pathname is an ASI marketing surface on any host. */
export function isAsiMarketingPath(pathname = '') {
  if (typeof pathname !== 'string') return false;
  const normalized = pathname.replace(/\/+$/, '') || '/';
  if (isAsiMarketingHost()) {
    if (normalized === '/') return true;
    const segment = normalized.replace(/^\/+/, '').split('/')[0];
    return MARKETING_SLUGS.includes(segment);
  }
  return (
    normalized === MARKETING_BASE ||
    normalized.startsWith(`${MARKETING_BASE}/`)
  );
}
